import { useState } from 'react';
import { AlertTriangle, FileSearch, Map as MapIcon, Search, XCircle } from 'lucide-react';
import Pagination from '@/client/components/common/Pagination';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  useRunSeoHealth,
  useSeoHealthQuery,
  type ApiSeoPage,
} from '@/client/api/marketing/seo';
import { EmptyState } from './components/marketing-ui';
import { MarketingPageShell } from './components/MarketingPageShell';
import {
  BAD_SERIES,
  SeoError,
  SeoKpi,
  SeoKpiRow,
  SeoNote,
  SeoPanel,
  SeoTrend,
  StatusPill,
  TD,
  TH,
  TR,
  fmtNumber,
} from './components/seo-ui';

export const TEMPLATE_LABEL: Record<string, string> = {
  home: 'Homepage',
  static: 'Section pages',
  brand: 'Brand pages',
  search: 'Search pages',
  landing: 'Category in location',
  category: 'Category pages',
  listing_market: 'Market listings',
  listing_homes: 'Homes listings',
  listing_locals: 'Locals listings',
  cms: 'Content pages',
  sitemap: 'Sitemaps',
  robots: 'robots.txt',
  llms: 'llms.txt',
  other: 'Other',
};

const CHECK_LABEL: Record<string, string> = {
  status_200: 'Does not return 200',
  canonical_self: 'Canonical points to another URL',
  canonical_present: 'No canonical tag',
  indexable: 'Marked noindex',
  title_present: 'No title',
  title_specific: 'Generic fallback title',
  title_length: 'Title length',
  title_unique: 'Title shared with other pages',
  description_present: 'No meta description',
  description_length: 'Description length',
  og_url_self: 'og:url is not this page',
  jsonld_valid: 'Structured data does not parse',
  jsonld_type: 'Expected structured data missing',
};

function path(url: string) {
  try {
    const u = new URL(url);
    return u.pathname + u.search;
  } catch {
    return url;
  }
}

function PageRow({ page }: { page: ApiSeoPage }) {
  const failed = page.checks.filter((c) => !c.ok);
  return (
    <tr className={cn(TR, 'align-top')}>
      <td className={TD}>
        <StatusPill status={page.verdict} />
      </td>
      <td className={cn(TD, 'whitespace-nowrap text-gray-600')}>{TEMPLATE_LABEL[page.template] ?? page.template}</td>
      <td className={cn(TD, 'max-w-sm break-all')}>
        <a href={page.url} target="_blank" rel="noreferrer" className="text-gray-900 hover:underline">
          {path(page.url)}
        </a>
      </td>
      <td className={cn(TD, 'text-gray-700')}>
        <ul className="space-y-1">
          {failed.map((c) => (
            <li key={c.id}>
              <span className={cn('font-medium', c.level === 'fail' ? 'text-rose-700' : 'text-amber-800')}>
                {CHECK_LABEL[c.id] ?? c.id}
              </span>
              {c.detail && <span className="break-all text-gray-500">: {c.detail}</span>}
            </li>
          ))}
        </ul>
      </td>
    </tr>
  );
}

/**
 * What a search engine receives from our own pages, checked nightly: every
 * sitemap, plus a sample of pages per template (ff-api service/seo_health.py).
 * It reports whether pages can be indexed as intended, not how they rank.
 */
export default function SeoHealthPage() {
  const { data, isLoading, error } = useSeoHealthQuery();
  const runNow = useRunSeoHealth();
  const run = data?.run;
  const summary = run?.summary;
  const problems = (run?.pages ?? [])
    .filter((p) => p.verdict !== 'pass')
    .sort((a, b) => (a.verdict === b.verdict ? 0 : a.verdict === 'fail' ? -1 : 1));
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const pageCount = Math.max(1, Math.ceil(problems.length / limit));
  const current = Math.min(page, pageCount);
  if (current !== page) setPage(current);
  const visible = problems.slice((current - 1) * limit, current * limit);
  const trend = (data?.history ?? []).map((h) => ({
    day: h.at.slice(0, 10),
    fail: h.summary.fail,
    warn: h.summary.warn,
  }));

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-gray-600">
          {run
            ? `Last checked ${new Date(run.at).toLocaleString()} against ${run.base_url ?? 'the live site'}.`
            : 'Checked nightly: every sitemap, and a sample of pages per page type.'}
        </p>
        <Button
          variant="outline"
          size="sm"
          onClick={() => runNow.mutate({})}
          disabled={runNow.isPending || runNow.isSuccess}
        >
          {runNow.isSuccess ? 'Queued: results in about 3 minutes' : 'Run check now'}
        </Button>
      </div>

      {error && <SeoError>Could not load SEO page health.</SeoError>}
      {runNow.isError && <SeoError>Could not start the check.</SeoError>}

      {!isLoading && !run ? (
        <EmptyState label="No check has run yet. The first one runs overnight, or start one now." />
      ) : (
        <>
          <SeoKpiRow>
            <SeoKpi label="Pages checked" value={summary?.pages} icon={FileSearch} loading={isLoading} />
            <SeoKpi
              label="Failing"
              value={summary?.fail}
              icon={XCircle}
              tone={summary?.fail ? 'bad' : 'good'}
              hint="Not indexable as intended"
              loading={isLoading}
            />
            <SeoKpi label="Warnings" value={summary?.warn} icon={AlertTriangle} hint="Indexable, could be better" loading={isLoading} />
            <SeoKpi
              label="Sitemaps failing"
              value={summary ? `${summary.sitemaps_failing} of ${summary.sitemaps}` : undefined}
              icon={MapIcon}
              tone={summary?.sitemaps_failing ? 'bad' : 'good'}
              hint={summary ? `${fmtNumber(summary.sitemap_urls)} URLs listed` : undefined}
              loading={isLoading}
            />
          </SeoKpiRow>

          <div className="grid gap-4 lg:grid-cols-2">
            <SeoPanel title="Failing pages per check" subtitle={`Last ${trend.length} checks; lower is better`}>
              <SeoTrend data={trend} series={[{ key: 'fail', name: 'Failing pages', color: BAD_SERIES, kind: 'bar' }]} />
            </SeoPanel>

            <SeoPanel title="By page type" subtitle="Five sampled pages of each type per check" flush>
              <table className="w-full text-sm">
                <thead>
                  <tr>
                    <th className={TH}>Page type</th>
                    <th className={cn(TH, 'text-right')}>Checked</th>
                    <th className={cn(TH, 'text-right')}>Pass</th>
                    <th className={cn(TH, 'text-right')}>Warn</th>
                    <th className={cn(TH, 'text-right')}>Fail</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(summary?.by_template ?? {}).map(([template, row]) => (
                    <tr key={template} className={TR}>
                      <td className={cn(TD, 'text-gray-900')}>{TEMPLATE_LABEL[template] ?? template}</td>
                      <td className={cn(TD, 'text-right tabular-nums text-gray-600')}>{row.pages}</td>
                      <td className={cn(TD, 'text-right tabular-nums text-gray-600')}>{row.pass}</td>
                      <td className={cn(TD, 'text-right tabular-nums text-gray-600')}>{row.warn}</td>
                      <td className={cn(TD, 'text-right tabular-nums', row.fail > 0 ? 'font-semibold text-rose-700' : 'text-gray-600')}>
                        {row.fail}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </SeoPanel>
          </div>

          <SeoPanel title={`Pages with problems (${problems.length})`} subtitle="Failures first" flush>
            {problems.length === 0 ? (
              <p className="px-4 py-6 text-sm text-gray-600">Every sampled page passed.</p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr>
                    <th className={TH}>Result</th>
                    <th className={TH}>Page type</th>
                    <th className={TH}>Page</th>
                    <th className={TH}>What is wrong</th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map((p) => (
                    <PageRow key={p.url} page={p} />
                  ))}
                </tbody>
              </table>
            )}
            {problems.length > 0 && (
              <div className="border-t border-gray-100 px-4 py-3">
                <Pagination
                  total={problems.length}
                  page={current}
                  limit={limit}
                  itemsPerPage={limit}
                  setPage={setPage}
                  setItemsPerPage={(n) => {
                    setLimit(n);
                    setPage(1);
                  }}
                  showSelect
                  selectOptions={[10, 20, 50]}
                  compact
                />
              </div>
            )}
          </SeoPanel>

          <SeoPanel title="Sitemaps" subtitle={`${fmtNumber(summary?.sitemap_urls)} URLs listed across ${summary?.sitemaps ?? 0} sitemaps`} flush>
            <table className="w-full text-sm">
              <thead>
                <tr>
                  <th className={TH}>Sitemap</th>
                  <th className={TH}>Page type</th>
                  <th className={cn(TH, 'text-right')}>URLs</th>
                  <th className={cn(TH, 'text-right')}>Previous</th>
                  <th className={TH}>Status</th>
                </tr>
              </thead>
              <tbody>
                {(run?.sitemaps ?? []).map((s) => (
                  <tr key={s.url} className={TR}>
                    <td className={cn(TD, 'max-w-sm break-all text-gray-900')}>{path(s.url)}</td>
                    <td className={cn(TD, 'text-gray-600')}>{TEMPLATE_LABEL[s.template] ?? s.template}</td>
                    <td className={cn(TD, 'text-right tabular-nums')}>{fmtNumber(s.urls)}</td>
                    <td className={cn(TD, 'text-right tabular-nums text-gray-500')}>{fmtNumber(s.previous_urls)}</td>
                    <td className={TD}>
                      <StatusPill status={s.ok ? 'pass' : 'fail'} label={s.ok ? 'OK' : s.detail} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </SeoPanel>

          <SeoNote>
            A failing page is one a search engine would not index as intended: it errors, redirects, is marked noindex,
            points its canonical elsewhere or lacks its structured data. A warning is indexable but weaker than it could
            be.
          </SeoNote>
        </>
      )}
    </div>
  );
}

/** `/admin/seo/health` — the page under the SEO menu. */
export function SeoHealthRoute() {
  return (
    <MarketingPageShell
      title="Page health"
      description="What search engines and AI crawlers receive from our own pages: every sitemap, and a sample of pages per page type, checked nightly."
      crumbs={[{ label: 'SEO', href: '/admin/seo' }, { label: 'Page health' }]}
      icon={Search}
    >
      <SeoHealthPage />
    </MarketingPageShell>
  );
}
